import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { CardContent, CardV2, CardHeader } from "../../components/CardV2/CardV2";
import SectionTitle from "../../components/SectionTitle/SectionTitle";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import FileUploadWrapper from "../../components/FileUploadWrapper/FileUploadWrapper";
import CommentCard from "../../components/CommentCard/CommentCard";
import TextArea from "../../components/TextArea/TextArea";
import { CommissionComment } from "../../api/ComissionContractsAPI";
import FileList from "../../components/FileList/FileList";
import CommissionContractAPI from "../../api/CommissionContractAPI";
import { toastService } from "../../services/ToastService";
import PriceField from "../../components/PriceField/PriceField";
import InputNumber from "../../components/InputNumber/InputNumber";
import CONFIG from "../../config/config";
import { CommissionStatus } from "../../types/commission-statuses";
import Pill, { PillColor } from "../../components/Pill/Pill";
import { useTranslation } from "react-i18next";
import Tooltip from "../../components/Tooltip/Tooltip";
import InfoHint from "../../components/InfoHint/InfoHint";
import InputShortText from "../../components/InputShortText/InputShortText";
import PhoneNumberInput from "../../components/PhoneNumberInput/PhoneNumberInput";
import Subtitle from "../../components/Subtitle/Subtitle";
import FlyoutMenu from "../../components/FlyoutMenu/FlyoutMenu";
import { Icon } from "../../components/Icon/Icon";
import { isSupportedLanguage } from "../../shared/supported-languages";
import { uploadFile } from '../../utils/CommissionFileUploadUtils';
import { urlAndS3Encode } from "../../shared/fileUtils";
import SlideOver from "../../components/SlideOver/SlideOver";
import CommissionPaymentPlanDisplay from "../../components/CommissionPaymentPlan/CommissionPaymentPlanDisplay";
import { PaymentPlan, PaymentPlanItem, PaymentStatus } from "../../types/payment-plan";
import CommissionPaymentPlansAPI from "../../api/CommissionPaymentPlansAPI";

interface ContractVersion {
  pdf: string;
  createdAt: string;
}

interface Addon {
  name: string;
  description: string;
  cost: number;
  currency: string;
}

interface CommissionContract {
  id?: number;
  brideName: string;
  brideSurname: string;
  bridePhone: string;
  brideEmail: string;
  brideSocial: string;
  dress: string;
  dressSize: string;
  purchaseYear: string;
  purchasePriceAmount?: number;
  purchasePriceCurrency?: string;
  desiredSalePriceAmount?: number;
  desiredSalePriceCurrency?: string;
  damagesNotes?: string;
  notes?: string;
  addons?: Addon[];
  status: string;
  itemReceived: boolean;
  acceptedDate: string;
  endDate: string;
  payment: string;
  contracts?: ContractVersion[];
  attachments?: string[];
  trackingId?: string;
  submissionDate?: string;
  paymentPlan?: PaymentPlan;
  requiresPayment?: boolean;
  storeInfo?: {
    logoUrl?: string;
    brandName?: string;
    companyName?: string;
    companyAddress?: string;
    deliveryAddress?: string;
    phoneNumber?: string;
    email?: string;
    instagram?: string;
  };
  onlinePaymentsEnabled?: boolean;
}

const getCommissionStatusColor = (status: string | undefined): PillColor => {
  switch (status) {
    case CommissionStatus.ACCEPTED:
      return 'green';
    case CommissionStatus.PENDING:
      return 'blue';
    case CommissionStatus.REJECTED:
      return 'red';
    case CommissionStatus.COMPLETED:
      return 'yellow';
    default:
      return 'grey';
  }
};

export default function CommissionTrackPage() {
  const { trackingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState<CommissionContract | null>(null);
  const [originalContract, setOriginalContract] = useState<CommissionContract | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<CommissionComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoadingComment, setIsLoadingComment] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const isEditable = contract?.status === 'PENDING' || contract?.status === 'MISSING_REQUIREMENTS';
  const canDownloadPdf = contract?.status === 'ACCEPTED';

  const languages = CONFIG.SUPPORTED_LANGUAGES.map(l => {
    return {
      code: l.code,
      name: '',
      onClick: () => i18n.changeLanguage(l.code),
      icon: <Icon url={l.icon} cssStyles="w-5 rounded h-3"/>,
    }
  });

  const languageMenuItems = languages.map((lang) => ({
    text: lang.name,
    headline: lang.name,
    onClick: () => i18n.changeLanguage(lang.code),
    isDisabled: false,
    icon: lang.icon,
  }));

  useEffect(() => {
    const fetchContract = async () => {
      try {
        if (!trackingId) {
          throw new Error(t('commission_track_page.errors_no_tracking_id') || 'No tracking ID provided');
        }
        const response = await CommissionContractAPI.getContractByTrackingId(trackingId);
        const contractData = response.data;
        
        // Check if the contract data is null or empty
        if (!contractData || !contractData.id) {
          setContract(null);
          setOriginalContract(null);
          setError(null);
          return;
        }
        
        // Map the API response to the form data (use separate brideName and brideSurname fields)
        const mappedContract = {
          id: contractData.id,
          brideName: contractData.brideName || '',
          brideSurname: contractData.brideSurname || '',
          bridePhone: contractData.bridePhone || '',
          brideEmail: contractData.brideEmail || '',
          brideSocial: contractData.brideSocial || '',
          dress: contractData.dress || '',
          dressSize: contractData.dressSize || '',
          purchaseYear: contractData.purchaseYear || '',
          purchasePriceAmount: contractData.purchasePriceAmount,
          purchasePriceCurrency: contractData.purchasePriceCurrency,
          desiredSalePriceAmount: contractData.desiredSalePriceAmount,
          desiredSalePriceCurrency: contractData.desiredSalePriceCurrency,
          damagesNotes: contractData.damagesNotes || '',
          notes: contractData.notes || '',
          addons: contractData.addons || [],
          status: contractData.status || 'PENDING',
          itemReceived: contractData.itemReceived || false,
          acceptedDate: contractData.acceptedDate || '',
          endDate: contractData.endDate || '',
          payment: contractData.payment || 'NOT_PAID',
          attachments: contractData.attachments || [],
          contracts: contractData.pdf ? [{ pdf: contractData.pdf, createdAt: contractData.acceptedDate || new Date().toISOString() }] : [],
          trackingId: contractData.trackingId,
          storeInfo: contractData.storeInfo,
          onlinePaymentsEnabled: contractData.onlinePaymentsEnabled,
          paymentPlan: contractData.paymentPlan ? {
            id: contractData.paymentPlan.id,
            contractId: contractData.id || 0,
            totalAmount: contractData.paymentPlan.totalAmount,
            currency: contractData.paymentPlan.currency,
            createdAt: new Date().toISOString(),
            updatedAt: contractData.paymentPlan.paymentSplits?.[0]?.updatedAt || new Date().toISOString(),
            items: contractData.paymentPlan.paymentSplits?.map((split: any) => ({
              id: split.id,
              concept: split.concept || 'DROP_FEE',
              amount: split.amount,
              currency: contractData.paymentPlan!.currency,
              dueDate: split.dueDate,
              validForDays: split.validForDays,
              status: (split.paid ? PaymentStatus.PAID : PaymentStatus.NOT_PAID) as PaymentStatus,
              paidDate: split.paidDate,
              paymentMethod: split.paymentMethod,
              invoiceReference: split.invoiceReference,
              description: split.description,
              isRequired: true,
              isDisabled: split.isDisabled || false,
              createdAt: new Date().toISOString(),
              updatedAt: split.updatedAt
            })) || []
          } : undefined
        };
        
        setContract(mappedContract);
        setOriginalContract(JSON.parse(JSON.stringify(mappedContract)));
        setHasChanges(false);
      } catch (err: any) {
        // Handle 404 errors specifically
        if (err.response?.status === 404) {
          setContract(null);
          setOriginalContract(null);
          setError(null);
        } else {
          setError(err.response?.data?.message || (t('commission_track_page.errors_failed_to_load') || 'Failed to load contract information'));
          toastService.showToast(t('commission_track_page.errors_failed_to_load') || 'Failed to load contract information', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
  }, [trackingId, t]);

  const checkForChanges = (currentContract: CommissionContract | null, originalContract: CommissionContract | null) => {
    if (!currentContract || !originalContract) return false;
    
    if (currentContract.brideName !== originalContract.brideName ||
        currentContract.brideSurname !== originalContract.brideSurname ||
        currentContract.bridePhone !== originalContract.bridePhone ||
        currentContract.brideEmail !== originalContract.brideEmail ||
        currentContract.brideSocial !== originalContract.brideSocial ||
        currentContract.dress !== originalContract.dress ||
        currentContract.dressSize !== originalContract.dressSize ||
        currentContract.purchaseYear !== originalContract.purchaseYear ||
        currentContract.purchasePriceAmount !== originalContract.purchasePriceAmount ||
        currentContract.purchasePriceCurrency !== originalContract.purchasePriceCurrency ||
        currentContract.desiredSalePriceAmount !== originalContract.desiredSalePriceAmount ||
        currentContract.desiredSalePriceCurrency !== originalContract.desiredSalePriceCurrency ||
        currentContract.damagesNotes !== originalContract.damagesNotes ||
        currentContract.notes !== originalContract.notes) {
      return true;
    }
    
    if (currentContract.addons?.length !== originalContract.addons?.length) return true;
    if (currentContract.addons && originalContract.addons) {
      for (let i = 0; i < currentContract.addons.length; i++) {
        const current = currentContract.addons[i];
        const original = originalContract.addons[i];
        if (!original || 
            current.name !== original.name ||
            current.description !== original.description ||
            current.cost !== original.cost ||
            current.currency !== original.currency) {
          return true;
        }
      }
    }
    
    if (currentContract.attachments?.length !== originalContract.attachments?.length) return true;
    if (currentContract.attachments && originalContract.attachments) {
      for (let i = 0; i < currentContract.attachments.length; i++) {
        if (currentContract.attachments[i] !== originalContract.attachments[i]) {
          return true;
        }
      }
    }
    
    return false;
  };

  useEffect(() => {
    if (contract && originalContract) {
      const changes = checkForChanges(contract, originalContract);
      setHasChanges(changes);
    }
  }, [contract, originalContract]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  useEffect(() => {
    const fetchComments = async () => {
      if (!trackingId) return;
      try {
        const response = await CommissionContractAPI.getPublicComments(trackingId);
        setComments(response.data);
      } catch (error) {
        toastService.showToast(t('commission_track_page.errors_failed_to_load_comments') || 'Failed to load comments', 'error');
      }
    };

    fetchComments();
  }, [trackingId, t]);

  const handleAddComment = async () => {
    if (!trackingId || !newComment.trim()) return;
    
    setIsLoadingComment(true);
    try {
      await CommissionContractAPI.addPublicComment(trackingId, {
        comment: newComment.trim()
      });
      setNewComment('');
      // Refresh comments
      const response = await CommissionContractAPI.getPublicComments(trackingId);
      setComments(response.data);
      toastService.showToast(t('commission_track_page.success.comment_added') || 'Comment added', 'success');
    } catch (error) {
      toastService.showToast(t('commission_track_page.errors.failed_to_add_comment') || 'Failed to add comment', 'error');
    } finally {
      setIsLoadingComment(false);
    }
  };

  const handleSave = useCallback(async () => {
    if (!contract || !isEditable || !trackingId) return;
    
    setIsSaving(true);
    try {
      // Map the contract data to the expected API format
      const contractData = {
        brideName: contract.brideName,
        brideSurname: contract.brideSurname,
        bridePhone: contract.bridePhone,
        brideEmail: contract.brideEmail,
        brideSocial: contract.brideSocial,
        dress: contract.dress,
        dressSize: contract.dressSize,
        purchaseYear: contract.purchaseYear,
        purchasePriceAmount: contract.purchasePriceAmount || 0,
        purchasePriceCurrency: contract.purchasePriceCurrency || 'PLN',
        desiredSalePriceAmount: contract.desiredSalePriceAmount || 0,
        desiredSalePriceCurrency: contract.desiredSalePriceCurrency || 'PLN',
        damagesNotes: contract.damagesNotes || '',
        notes: contract.notes || '',
        addons: contract.addons || [],
        attachments: contract.attachments || [],
        itemReceived: contract.itemReceived || false
      };

      await CommissionContractAPI.updateContract(trackingId, contractData);
      
      // Update the original contract to reflect the saved state
      setOriginalContract(JSON.parse(JSON.stringify(contract)));
      setHasChanges(false);
      
      toastService.showToast(t('commission_track_page.success.changes_saved') || 'Changes saved', 'success');
    } catch (error) {
      console.error('Error saving contract:', error);
      toastService.showToast(t('commission_track_page.errors.failed_to_save') || 'Failed to save changes', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [contract, isEditable, trackingId, t]);

  // Keyboard shortcut for saving (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasChanges && isEditable && !isSaving) {
          handleSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasChanges, isEditable, isSaving, handleSave]);

  const handleDownloadPDF = async () => {
    if (!trackingId) return;
    
    try {
        const response = await CommissionContractAPI.downloadPDF(trackingId);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `commission-contract-${trackingId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading PDF:', error);
        toastService.showToast(t('commission_track_page.errors.failed_to_download_pdf') || 'Failed to download PDF', 'error');
    }
  };

  const handlePayNow = async (paymentItem: PaymentPlanItem) => {
    try {
      if (!contract?.trackingId || !paymentItem?.id) return;
      if (paymentItem.status === PaymentStatus.PAID || paymentItem.isDisabled) return;

      toastService.showToast(t('commission_track_page.payment_redirect') || 'Redirecting to payment...', 'success');

      const checkoutUrl = (await CommissionPaymentPlansAPI.createSplitCheckout(contract.trackingId, paymentItem.id)).data;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toastService.showToast(t('commission_track_page.errors_failed_to_start_payment') || 'Unable to start payment. Please try again later.', 'error');
      }
    } catch (e) {
      toastService.showToast(t('commission_track_page.errors_failed_to_start_payment') || 'Unable to start payment. Please try again later.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-6">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('commission_track_page.contract_not_found_title') || 'Contract Not Found'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {contract === null 
              ? (t('commission_track_page.contract_not_found_message') || 'The commission contract you are looking for could not be found. Please check the tracking ID and try again.')
              : (error || t('commission_track_page.contract_not_found_message') || 'The commission contract you are looking for could not be found. Please check the tracking ID and try again.')
            }
          </p>
          {trackingId && (
            <div className="bg-gray-50 rounded-md p-3 mb-4">
              <p className="text-xs text-gray-600 mb-1">
                {t('commission_track_page.tracking_id_label') || 'Tracking ID:'}
              </p>
              <p className="text-sm font-mono text-gray-800">{trackingId}</p>
            </div>
          )}
          <div className="text-xs text-gray-400">
            {t('commission_track_page.contact_support_message') || 'If you believe this is an error, please contact support.'}
          </div>
          <div className="flex justify-center mb-6">
            <FlyoutMenu
              menuItems={languageMenuItems}
              trigger={
                isSupportedLanguage(i18n.language)
                  ? languages.filter(l => l.code === i18n.language)[0]?.icon
                  : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon
              }
              mobileTrigger={isSupportedLanguage(i18n.language)
                ? languages.filter(l => l.code === i18n.language)[0]?.icon
                : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon}
            />
          </div>
        </div>
      </div>
    );
  }

  // Photo removal logic
  const removePhoto = (field: 'attachments', fileName: string) => {
    if (!contract) return;
    setContract({
      ...contract,
      [field]: (contract[field] || []).filter((f: string) => f !== fileName)
    });
  };

  //
  const handleFileChange = async (field: 'attachments', files: File[]) => {
    if (!contract || !trackingId) return;
    
    setIsUploading(true);
    try {
      const uploadedUrls: string[] = [];
      
      for (const file of files) {
        try {
          const fileWithS3Encoding = urlAndS3Encode(file.name);
          const displayFileName = decodeURI(fileWithS3Encoding);
          
          const uploadResult = await uploadFile({
            fileName: fileWithS3Encoding,
            file,
            trackingId,
            isPublic: true
          });
          
          const fileUrl = uploadResult.url;
          
          uploadedUrls.push(fileUrl);
          toastService.showToast(`${t("toast.success.add_file") || 'File added'}: ${displayFileName}`, 'success');
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          toastService.showToast(`${t("toast.errors.add_file") || 'Failed to add file'}: ${file.name}`, 'error');
        }
      }
      
      // Update contract with new file URLs
      if (uploadedUrls.length > 0) {
        setContract({
          ...contract,
          [field]: [...(contract[field] || []), ...uploadedUrls]
        });
      }
    } catch (error) {
      console.error('Error processing files:', error);
      toastService.showToast(t('commission_track_page.errors.failed_to_upload_files') || 'Failed to upload files', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Fixed banner for unsaved changes */}
      {hasChanges && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 border-b border-amber-600 shadow-lg">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-center gap-2 text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">
                {t('commission_track_page.unsaved_changes') || 'You have unsaved changes'}
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div className="p-4 pt-20">
        <CardV2 className="max-w-4xl mx-auto">
          <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="mb-6">
            {/* Top row: Logo and Language */}
            <div className="flex justify-between items-center mb-4">
              {/* Store Logo */}
              {contract?.storeInfo?.logoUrl && (
                <div className="flex-shrink-0">
                  <img 
                    src={contract.storeInfo.logoUrl} 
                    alt="Store Logo" 
                    className="h-10 w-auto max-w-32 object-contain" 
                  />
                </div>
              )}
              
              {/* Language Selector */}
              <div className="flex-shrink-0">
                <FlyoutMenu
                  menuItems={languageMenuItems}
                  trigger={
                    isSupportedLanguage(i18n.language)
                      ? languages.filter(l => l.code === i18n.language)[0]?.icon
                      : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon
                  }
                  mobileTrigger={isSupportedLanguage(i18n.language)
                    ? languages.filter(l => l.code === i18n.language)[0]?.icon
                    : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon}
                />
              </div>
            </div>
            
            {/* Second row: Page Title - Full width on mobile */}
            <div className="text-center">
              <Subtitle text={t('commission_track_page.title') || 'Commission Contract'} />
            </div>
          </div>

          {/* Store info */}
          {contract?.storeInfo && (
            <div className="text-center text-sm text-muted-foreground mb-4 space-y-1">
              <div className="font-semibold text-gray-700 mb-1">{contract.storeInfo.brandName || contract.storeInfo.companyName}</div>
              <div className="text-xs text-gray-500 mb-2">{contract.storeInfo.companyAddress || contract.storeInfo.deliveryAddress}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center text-xs">
                {contract.storeInfo.phoneNumber && <span className="text-gray-600">{contract.storeInfo.phoneNumber}</span>}
                {contract.storeInfo.email && <span className="text-gray-600">{contract.storeInfo.email}</span>}
                {contract.storeInfo.instagram && <a href={`https://instagram.com/${contract.storeInfo.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">{contract.storeInfo.instagram}</a>}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-1 gap-6">
            {/* Contract data and photos */}
            <div>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <SectionTitle text={t('commission_track_page.contract_data') || 'Contract data'} />
                  <div className="flex items-center gap-2">
                    <PrimaryButton 
                      text={t('commission_track_page.download_pdf') || 'Download contract'}
                      fn={handleDownloadPDF}
                      isDisabled={!canDownloadPdf}
                    />
                    {!canDownloadPdf && (
                      <InfoHint 
                        content={t('commission_track_page.pdf_requires_acceptance') || 'PDF available after the contract is accepted.'}
                      />
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status and item received status row */}
                <div className="flex flex-wrap gap-4 items-center mb-2">
                  {/* General status */}
                  <Tooltip content={t(`commission.status.${contract?.status?.toLowerCase()}`) || contract?.status}>
                    <Pill 
                      text={t(`commission.status.${contract?.status?.toLowerCase()}`) || contract?.status || ''} 
                      color={getCommissionStatusColor(contract?.status)}
                    />
                  </Tooltip>
                  
                  {/* Item received status */}
                  {contract && (
                    <Tooltip content={
                      contract.itemReceived 
                        ? (t('commission_track_page.item_received_title') || 'Item Received')
                        : (t('commission_track_page.item_not_received_title') || 'Item Not Yet Received')
                    }>
                      <Pill 
                        text={contract.itemReceived 
                          ? (t('commission_track_page.item_received') || 'Item Received')
                          : (t('commission_track_page.item_not_received') || 'Item Not Received')
                        } 
                        color={contract.itemReceived ? 'green' : 'yellow'}
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputShortText
                    label={t('commission_track_page.first_name') || 'First name'}
                    defaultValue={contract?.brideName || ''}
                    isDisabled={!isEditable}
                    onChange={e => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        brideName: e.target.value
                      });
                    }}
                  />
                  <InputShortText
                    label={t('commission_track_page.last_name') || 'Last name'}
                    defaultValue={contract?.brideSurname || ''}
                    isDisabled={!isEditable}
                    onChange={e => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        brideSurname: e.target.value
                      });
                    }}
                  />
                  <PhoneNumberInput
                    label={t('commission_track_page.phone') || 'Phone'}
                    defaultValue={contract?.bridePhone || ''}
                    onChange={(value) => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        bridePhone: value
                      });
                    }}
                    isDisabled={!isEditable}
                  />
                  <InputShortText
                    label={t('commission_track_page.email') || 'Email'}
                    defaultValue={contract?.brideEmail || ''}
                    isDisabled={!isEditable}
                    onChange={e => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        brideEmail: e.target.value
                      });
                    }}
                  />
                  <InputShortText
                    label={t('commission_track_page.social_media') || 'Social media'}
                    defaultValue={contract?.brideSocial || ''}
                    isDisabled={!isEditable}
                    onChange={e => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        brideSocial: e.target.value
                      });
                    }}
                  />
                  <InputShortText
                    label={t('commission_track_page.dress_model') || 'Dress model (or Salon)'}
                    defaultValue={contract?.dress || ''}
                    isDisabled={!isEditable}
                    onChange={e => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        dress: e.target.value
                      });
                    }}
                  />
                  <InputShortText
                    label={t('commission_track_page.dress_size') || 'Dress size'}
                    defaultValue={contract?.dressSize || ''}
                    isDisabled={!isEditable}
                    onChange={e => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        dressSize: e.target.value
                      });
                    }}
                  />
                  <InputNumber 
                    label={t('commission_track_page.purchase_year') || 'Purchase year'}
                    defaultValue={isEditable ? parseInt(contract?.purchaseYear || '0') : parseInt(contract?.purchaseYear || '0')} 
                    onChange={e => {
                      if (!contract || !isEditable) return;
                      setContract({
                        ...contract,
                        purchaseYear: e.target.value
                      });
                    }}
                    isDisabled={!isEditable}
                    validationFn={(value) => value >= 1900 && value <= new Date().getFullYear()}
                    errorMessage="errors.invalid_year"
                  />
                  <div className="md:col-span-2">
                    <TextArea
                      label={t('commission_track_page.dress_measurements') || 'Dress measurements'}
                      defaultValue={contract?.notes || ''}
                      onChange={e => {
                        if (!contract || !isEditable) return;
                        setContract({
                          ...contract,
                          notes: e.target.value
                        });
                      }}
                      placeholder={t('commission_track_page.dress_measurements_placeholder') || `
                    Bust:.................................................................................................
Waist:.................................................................................................
Hips: .............................................................................................
Height: ...............................................................................................`}
                      isDisabled={!isEditable}
                      helperText={t('commission_track_page.dress_measurements_helper') || 'Enter exact dress measurements to help match future clients'}
                    />
                  </div>
                  <div>
                    <PriceField
                      label={t('commission_track_page.purchase_price') || 'Purchase price in salon'}
                      currency={contract?.purchasePriceCurrency || 'PLN'}
                      price={contract?.purchasePriceAmount || 0}
                      currencies={CONFIG.SUPPORTED_CURRENCIES}
                      onCurrencyChange={(currency) => {
                        if (!contract || !isEditable) return;
                        setContract({
                          ...contract,
                          purchasePriceCurrency: currency
                        });
                      }}
                      onPriceChange={(price) => {
                        if (!contract || !isEditable) return;
                        setContract({
                          ...contract,
                          purchasePriceAmount: price
                        });
                      }}
                      isDisabled={!isEditable}
                    />
                  </div>
                  <div>
                    <PriceField
                      label={t('commission_track_page.desired_sale_price') || 'Desired sale price range'}
                      currency={contract?.desiredSalePriceCurrency || 'PLN'}
                      price={contract?.desiredSalePriceAmount || 0}
                      currencies={CONFIG.SUPPORTED_CURRENCIES}
                      onCurrencyChange={(currency) => {
                        if (!contract || !isEditable) return;
                        setContract({
                          ...contract,
                          desiredSalePriceCurrency: currency
                        });
                      }}
                      onPriceChange={(price) => {
                        if (!contract || !isEditable) return;
                        setContract({
                          ...contract,
                          desiredSalePriceAmount: price
                        });
                      }}
                      isDisabled={!isEditable}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <TextArea 
                      label={t('commission_track_page.damages_notes') || 'List of damages or notes before acceptance'}
                      defaultValue={contract?.damagesNotes || ''}
                      onChange={e => {
                        if (!contract || !isEditable) return;
                        setContract({
                          ...contract,
                          damagesNotes: e.target.value
                        });
                      }}
                      maxLength={200}
                      placeholder={t('commission_track_page.damages_placeholder') || 'Describe any damages, stains, or other notes about the dress condition...'}
                      isDisabled={!isEditable}
                    />
                  </div>
                </div>
                
                {/* Payment Plan Section */}
                {contract?.paymentPlan && (
                  <div className="pt-4">
                    <SectionTitle text={t('commission_track_page.payment_status') || 'Payment status'} />
                    {contract?.status !== 'ACCEPTED' && (
                      <div className="mb-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-amber-800">
                                {t('commission_track_page.payment_warning_title') || 'Do Not Pay Yet'}
                              </h3>
                              <div className="mt-2 text-sm text-amber-700">
                                <p>
                                  {t('commission_track_page.payment_warning_message') || 'Please do not make any payments until your request has been accepted by the store. Wait for the contract status to change to "Accepted" before proceeding with payment.'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <CommissionPaymentPlanDisplay
                      paymentPlan={contract.paymentPlan}
                      onPayNow={handlePayNow}
                      showPayButtons={true}
                      isEditable={false}
                      onlinePaymentsEnabled={!!contract.onlinePaymentsEnabled}
                    />
                  </div>
                )}
                
                {/* Addons section */}
                <div className="mb-4 pt-4">
                  <SectionTitle text={t('commission_track_page.addons') || 'Add-ons'} />
                  {isEditable && (
                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          if (!contract) return;
                          const newAddon: Addon = {
                            name: '',
                            description: '',
                            cost: 0,
                            currency: 'PLN'
                          };
                          setContract({
                            ...contract,
                            addons: [...(contract.addons || []), newAddon]
                          });
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm underline mb-4"
                      >
                        {t('commission_track_page.add_addon') || '+ Add add-on'}
                      </button>
                    </div>
                  )}
                  <div className="space-y-3">
                    {contract?.addons && contract.addons.length > 0 ? (
                      contract.addons.map((addon, index) => (
                        <div key={index} className="border rounded p-3 bg-gray-50">
                          {isEditable ? (
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <InputShortText
                                  label={t('commission_track_page.addon_name') || 'Add-on name'}
                                  defaultValue={addon.name}
                                  onChange={e => {
                                    if (!contract) return;
                                    const newAddons = [...(contract.addons || [])];
                                    newAddons[index].name = e.target.value;
                                    setContract({ ...contract, addons: newAddons });
                                  }}
                                />
                              </div>
                              <div className="flex-1">
                                <InputShortText
                                  label={t('commission_track_page.addon_description') || 'Description'}
                                  defaultValue={addon.description}
                                  onChange={e => {
                                    if (!contract) return;
                                    const newAddons = [...(contract.addons || [])];
                                    newAddons[index].description = e.target.value;
                                    setContract({ ...contract, addons: newAddons });
                                  }}
                                />
                              </div>
                              <div className="flex-1">
                                <PriceField
                                  label={t('commission_track_page.addon_cost') || 'Cost'}
                                  currency={addon.currency}
                                  price={addon.cost}
                                  currencies={CONFIG.SUPPORTED_CURRENCIES}
                                  onCurrencyChange={currency => {
                                    if (!contract) return;
                                    const newAddons = [...(contract.addons || [])];
                                    newAddons[index].currency = currency;
                                    setContract({ ...contract, addons: newAddons });
                                  }}
                                  onPriceChange={price => {
                                    if (!contract) return;
                                    const newAddons = [...(contract.addons || [])];
                                    newAddons[index].cost = price;
                                    setContract({ ...contract, addons: newAddons });
                                  }}
                                  isDisabled={false}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!contract) return;
                                  const newAddons = (contract.addons || []).filter((_, i) => i !== index);
                                  setContract({ ...contract, addons: newAddons });
                                }}
                                className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors mt-6"
                                title={t('commission_track_page.remove_addon') || 'Remove add-on'}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-4">
                              <div className="flex-1 text-sm font-medium">{addon.name}</div>
                              <div className="flex-1 text-sm">{addon.description || (t('commission_track_page.addon_description') || 'Description')}</div>
                              <div className="flex-1 text-sm font-medium">{addon.cost} {addon.currency}</div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-4">
                        {t('commission_track_page.no_addons') || 'No add-ons'}
                      </div>
                    )}
                  </div>
                </div>
                {/* Dress photos */}
                <div className="mb-4 pt-4">
                  <SectionTitle text={t('commission_track_page.dress_photos') || 'Dress photos'} />
                  {isEditable && (
                    <div className="mb-4">
                      <FileUploadWrapper
                        label={t('commission_track_page.add_dress_photos') || 'Add dress photos'}
                        onChange={files => handleFileChange('attachments', files)}
                        allowedMultipleFiles={true}
                        maxSize={CONFIG.PUBLIC_MAX_FILE_UPLOAD}
                        allowedTypes={CONFIG.FILE_TYPES}
                      />
                      {isUploading && (
                        <div className="mt-2 text-sm text-blue-600 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          {t('commission_track_page.uploading_files') || 'Uploading files...'}
                        </div>
                      )}
                    </div>
                  )}
                  {(!contract?.attachments || contract.attachments.length === 0) && (
                    <div className="mt-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-amber-800">
                              {t('commission_track_page.no_photos_title') || 'Missing Dress Photos'}
                            </h3>
                            <div className="mt-2 text-sm text-amber-700">
                              <p>
                                {t('commission_track_page.no_photos_info') || 'There are no pictures attached of the dress. Please upload to help us move forward and accept your request.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <FileList
                    existingFiles={contract?.attachments || []}
                    onRemoveExisting={isEditable ? (url: string) => removePhoto('attachments', url) : undefined}
                    isDisabled={!isEditable}
                    resourceLinkType="public"
                    resourceLinkProps={{
                      trackingId: trackingId || ''
                    }}
                  />
                </div>
                {isEditable && (
                  <div className="flex justify-center gap-4 mt-4">
                    <PrimaryButton 
                      text={`${t('commission_track_page.view_comments') || 'View Comments'} (${comments.length})`}
                      fn={() => setIsCommentsOpen(true)}
                    />
                    <Tooltip content={`${t('commission_track_page.save_changes') || 'Save changes'} (Ctrl+S)`}>
                      <div>
                        <PrimaryButton 
                          text={isSaving ? (t('commission_track_page.saving') || 'Saving...') : (t('commission_track_page.save_changes') || 'Save changes')} 
                          fn={handleSave} 
                          isDisabled={isSaving || !hasChanges}
                        />
                      </div>
                    </Tooltip>
                  </div>
                )}
                {!isEditable && (
                  <div className="flex justify-center mt-4">
                    <PrimaryButton 
                      text={`${t('commission_track_page.view_comments') || 'View Comments'} (${comments.length})`}
                      fn={() => setIsCommentsOpen(true)}
                    />
                  </div>
                )}
              </CardContent>
            </div>
          </div>
        </div>
      </CardV2>

      {/* Comments SlideOver */}
      <SlideOver
        isOpen={isCommentsOpen}
        setIsOpen={setIsCommentsOpen}
        title={`${t('commission_track_page.comments') || 'Comments'} (${comments.length})`}
        primaryButton={{
          label: t('commission_track_page.add_comment') || 'Add Comment',
          onClick: handleAddComment
        }}
        secondaryButton={{
          label: t('global.close') || 'Close',
          onClick: () => setIsCommentsOpen(false)
        }}
        isLoading={isLoadingComment}
        hasErrors={!newComment.trim() || contract?.status === "REJECTED"}
      >
        <div className="space-y-4">
          {/* Comments list */}
          <div className="max-h-96 overflow-y-auto space-y-3 pb-4 px-2">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <CommentCard 
                  key={comment.id} 
                  comment={comment} 
                  type="commission"
                  isSelf={comment.writtenBy?.toLowerCase().trim() === `${contract?.brideName} ${contract?.brideSurname}`.toLowerCase().trim()}
                />
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                {t('commission_track_page.no_comments') || 'No comments yet'}
              </div>
            )}
          </div>
          
          {/* Add comment form */}
          <div className="space-y-2">
            <TextArea
              label={t('commission_track_page.add_comment') || 'Add comment'}
              defaultValue={newComment}
              onChange={e => setNewComment(e.target.value)}
              isDisabled={isLoadingComment || contract?.status === "REJECTED"}
              placeholder={t('commission_track_page.comment_placeholder') || 'Write your comment here...'}
            />
            <div className="text-xs text-gray-500">
              {contract?.status === "REJECTED" 
                ? t('commission_track_page.comments_disabled_rejected') || 'Comments are disabled for rejected contracts.'
                : t('commission_track_page.comment_instructions') || 'Your comment will be visible to the store staff.'
              }
            </div>
          </div>
        </div>
      </SlideOver>
    </div>
    </div>
  );
} 
